import _orderBy from 'lodash/orderBy';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';

// Store
import store from '../store/store';
import { setIndicatorsObject, setLevelList } from '../store/slices/admin2EstimatesSlice';

// Controllers
import mapController from '../controllers/MapControllers';

// Services
import { getAdmin1FeaturesWithGeometries, fetchAdmin2FeaturesWithGeometries } from '../services/getGeometries.service';
import { getSurveyData } from '../services/getSurveyData.service';

// Utils
import { getFeatureLayerID } from './map.utils';
import { getIndicatorKey } from './indicators.utils';

// Config
import { ADMIN1_REGION_ID_FIELD, DHSREGEN } from '../configs/map.configs';
import { SurveySelectedType } from '../configs/types';
import { IndicatorType } from '../configs/indicators.config';

/**
 * Update admin 1 client side features layer
 * @param {string} admin1ClientSideFeaturesLayerID - Admin 1 client side features layer ID
 * @param {SurveySelectedType | null} surveySelected - Selected survey
 * @param {Object} indicatorsObject - Indicators object from DHS API response
 * @param {string} adminSelected - Admin selected (['Admin1', 'Admin1', '5x5km'])
 */
export function updateAdmin1ClientSideFeaturesLayer(
  admin1ClientSideFeaturesLayerID: string,
  surveySelected: SurveySelectedType | null,
  indicatorsObject: any,
  adminSelected: string
) {
  if (mapController?.admin1ClientSideFeaturesLayer?.id !== admin1ClientSideFeaturesLayerID && surveySelected) {
    const countryCode = surveySelected?.DHSCC;
    const year = surveySelected?.year;
    getAdmin1FeaturesWithGeometries(countryCode, year).then(async (admin1FeaturesWithGeometries) => {
      if (import.meta.env.MODE === 'development') {
        console.log('getAdmin1Features:', admin1FeaturesWithGeometries);
      }

      handleLevelDropdown(admin1FeaturesWithGeometries);

      await mapController.setAdmin1Graphic(
        admin1FeaturesWithGeometries,
        indicatorsObject,
        adminSelected,
        surveySelected
      );
    });
  } else {
    if (import.meta.env.MODE === 'development') {
      console.log('Update admin level 1');
    }

    mapController.updateAdmin1Graphic(indicatorsObject);
  }
}

/**
 * Create admin 2 geometries from a union of admin 2 features, in order to have all bounadries line up
 * @param {string} countryCode - Country code (e.g. 'BF')
 * @return {Object} geometriesByAdmin1 - Object of ADM1_NAME keys and their unioned geometries values
 */
export const generateAdmin1UnionGeomatriesFromAdmin2Features = async (countryCode: string) => {
  const admin2Features = await fetchAdmin2FeaturesWithGeometries(countryCode);
  if (admin2Features && admin2Features.features && admin2Features.features.length > 0) {
    const geometriesByAdmin1 = admin2Features.features.reduce((acc: any, feature: any) => {
      const admin1NameLowerCase = feature.attributes.ADM1_NAME.toLowerCase();

      if (!acc[admin1NameLowerCase]) {
        acc[admin1NameLowerCase] = [];
      }

      acc[admin1NameLowerCase].push(feature.geometry);
      return acc;
    }, {});

    for (const [key, value] of Object.entries(geometriesByAdmin1)) {
      const union = geometryEngine.union(value as __esri.Geometry[]);
      geometriesByAdmin1[key] = union;
    }

    return geometriesByAdmin1;
  }
};

/**
 * Handle admin 1 client side features layer updates
 * @param {SurveySelectedType | null} surveySelected - Survey selected
 * @param {Object} indicatorsObject - Indicators object from DHS API response
 * @param {IndicatorType} indicatorSelected - Indicator selected
 * @param {string} adminSelected - Admin selected (e.g. ['Admin1', 'Admin1', '5x5km'])
 */
export function handleAdmin1ClientSideFeaturesLayerUpdates(
  surveySelected: SurveySelectedType | null,
  indicatorsObject: any,
  indicatorSelected: IndicatorType,
  adminSelected: string
) {
  const admin1ClientSideFeaturesLayerID = getFeatureLayerID();
  const indicatorKey = getIndicatorKey();
  const indicatorData = indicatorsObject[indicatorKey];
  if (!indicatorData) {
    if (surveySelected) {
      getSurveyData(surveySelected, indicatorSelected).then((surveySelectedData) => {
        const indicatorsObjectKeys = Object.keys(surveySelectedData);
        const dhsApiResponseProcessed: any = {};

        indicatorsObjectKeys.map((indicatorsObjectKey: string) => {
          const item = surveySelectedData[indicatorsObjectKey];
          const { CharacteristicLabel } = item;
          let newItem = item;

          if (CharacteristicLabel.startsWith('.')) {
            const replaceDots = CharacteristicLabel.replaceAll('.', '');
            newItem = {
              ...item,
              CharacteristicLabel: replaceDots,
            };
          }

          dhsApiResponseProcessed[indicatorsObjectKey] = newItem;
        });

        store.dispatch(setIndicatorsObject({ ...indicatorsObject, [indicatorKey]: dhsApiResponseProcessed }));
        updateAdmin1ClientSideFeaturesLayer(
          admin1ClientSideFeaturesLayerID,
          surveySelected,
          surveySelectedData,
          adminSelected
        );
      });
    }
  } else {
    updateAdmin1ClientSideFeaturesLayer(admin1ClientSideFeaturesLayerID, surveySelected, indicatorData, adminSelected);
  }
}

/**
 * Handles the level dropdown by sorting the admin1LevelList and dispatching it to the store.
 * @param {void | __esri.FeatureSet} admin1FeaturesWithGeometries - The admin1 features with geometries.
 * @returns {void} - No return value.
 */
export function handleLevelDropdown(admin1FeaturesWithGeometries: void | __esri.FeatureSet): void {
  // Extracts the admin1LevelList from the admin1FeaturesWithGeometries.
  const admin1LevelList: object[] | undefined = admin1FeaturesWithGeometries?.features.map((feature) => {
    return {
      level: feature.attributes[DHSREGEN],
      admin1RegionID: feature.attributes[ADMIN1_REGION_ID_FIELD],
    };
  });

  // Sorts the admin1LevelList by the 'level' attribute.
  const admin1LevelListSorted = _orderBy(admin1LevelList, ['level']);

  // Dispatches the sorted admin1LevelList to the store.
  store.dispatch(setLevelList(admin1LevelListSorted));
}
